import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"

import './index.scss'
import TabCarouselTab from "./components/TabCarouselTab"

const WIDTH_RATIO = .95

const TabCarousel = forwardRef(({ tabs, onTabSelect }, ref) => {
    const [activeTabIndex, setActiveTabIndex] = useState(0)
    const [startIndex, setStartIndex] = useState(0)

    const [tabWidths, setTabWidths] = useState({})
    const [outerContainerWidth, setOuterContainerWidth] = useState(0)

    const outerContainerRef = useRef(null)

    useEffect(() => {
        const outerContainerObserver = new ResizeObserver(entries => {
            setOuterContainerWidth(entries[0].contentRect.width)
        })

        outerContainerObserver.observe(outerContainerRef.current)

        return () => {
            outerContainerRef.current && outerContainerObserver.unobserve(outerContainerRef.current)
        }
    }, [])

    const registerTabWidth = (index, width) => {
        setTabWidths(x => ({ ...x, [index]: width }))
    }

    const maxTabCount = useMemo(() => {
        const widths = Object.values(tabWidths)

        if (widths.length !== tabs.length) {
            return tabs.length
        }

        widths.sort((a, b) => b - a)
        let sum = widths[0]

        for (let i = 1; i < tabs.length; i++) {
            if (sum + widths[i] > outerContainerWidth * WIDTH_RATIO) {
                return i
            }

            else sum += widths[i]
        }

        return tabs.length
    }, [tabWidths, outerContainerWidth])

    const shownTabs = useMemo(() => {
        const sum = []
        for (let i = 0; i < maxTabCount; i++) {
            let index = i + startIndex

            if (index >= tabs.length) {
                index -= maxTabCount + 1
            }

            sum.push({ ...tabs[index], index: i })
        }

        return sum
    }, [tabs, maxTabCount, startIndex])

    const activeTab = useMemo(() => tabs[activeTabIndex], [tabs, activeTabIndex])
    const setActiveTab = (selector) => setActiveTabIndex(tabs.findIndex(selector))
    useImperativeHandle(ref, () => ({ activeTab, setActiveTab }))

    const onSelect = (index) => {
        setActiveTabIndex(index)
        onTabSelect && onTabSelect(tabs[index])
    }

    const onArrowClick = (direction) => {
        let index = startIndex + direction

        if (index < 0) {
            index = 0
        }
        else if (index + maxTabCount > tabs.length) {
            index = index - 1
        }

        setStartIndex(index)
    }

    return (
        <div className="tab-carousel-outer-container m-top-20" ref={outerContainerRef}>
            <div className="tab-carousel-inner-container"    >
                <div className={"tab-carousel-arrow" + (startIndex < 1 ? ' disabled' : '')}>
                    <span className="material-symbols-outlined search-i" onClick={() => onArrowClick(-1)}>chevron_left</span>
                </div>

                <div className="tab-carousel-tabs nav nav-tabs" role='tablist'>
                    {shownTabs.map(tab => (
                        <TabCarouselTab
                            {...{tab, registerTabWidth, onSelect}}
                            active={tab.index === activeTabIndex}
                            key={tab.index}
                        />
                    ))}
                </div>

                <div className={"tab-carousel-arrow" + (startIndex + maxTabCount > tabs.length - 1 ? ' disabled' : '')}>
                    <span className="material-symbols-outlined search-i" onClick={() => onArrowClick(1)}>chevron_right</span>
                </div>
            </div>

            <div className="tab-content" style={{ width: '100%' }}>
                <div className="tab-panel">
                    {activeTab && activeTab.content}
                </div>
            </div>
        </div>
    )
})

export default TabCarousel
